import { useState, useEffect, useRef, useCallback } from "react";
import { useOnboarding } from "@/hooks/use-onboarding";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import {
  Building2, MapPin, ChevronRight, CheckCircle2, Rocket, Search,
  Loader2, Sparkles, Star, AlertTriangle, Shield, CreditCard,
  Check, RefreshCw, DollarSign, Users, X, Plus, Trophy, Mail, LogOut,
  Phone, Target, BarChart3, Eye, Pencil, Clock, Globe, Tag,
  CalendarDays, ListChecks, ArrowDown, Play,
} from "lucide-react";
import { SiGoogle } from "react-icons/si";
import { AriaChat } from "@/components/aria-chat";
import { useAuth } from "@/hooks/use-auth";
import { useLocationStore } from "@/lib/store";
// Stripe hosted-checkout — see BillingModal.


interface GbpLocation {
  resourceName: string;
  accountName?: string;
  title: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  rating: number;
  totalReviews: number;
  verificationState: string;
  gbpStatus: string;
  primaryCategory?: string;
  phone?: string;
  website?: string;
  placeId?: string;
  lat?: number;
  lng?: number;
}

type GoogleStatus = {
  configured: boolean;
  connected: boolean;
  googleEmail: string | null;
};

type GbpLocationsResponse = {
  connected: boolean;
  locations: GbpLocation[];
  error?: string;
  needsGbpScope?: boolean;
  needsReconnect?: boolean;
};

interface ProfileField {
  key: string;
  label: string;
  category: string;
  status: "complete" | "partial" | "incomplete";
  value?: string;
  tip: string;
}

function SuspendedDialog({
  open,
  onClose,
  onContinue,
  isSuspended,
}: {
  open: boolean;
  onClose: () => void;
  onContinue: () => void;
  isSuspended: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-md">
        <div className="flex flex-col items-center text-center space-y-4 py-2">
          <div className="w-16 h-16 rounded-full bg-brand-orange/10 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-brand-orange" />
          </div>
          <DialogTitle className="text-lg">
            {isSuspended
              ? "This profile is currently suspended."
              : "This profile needs to be verified."}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            {isSuspended
              ? "Your Google Business Profile is suspended and won't appear in Google Maps or Search until it's reinstated. We can help you appeal the suspension, but optimization features will be limited until it's resolved."
              : "Google requires you to verify your business before it appears in search results. We'll walk you through the verification process."}
          </p>
          <div className="flex gap-3 w-full pt-2">
            <Button variant="outline" className="flex-1" onClick={onClose} data-testid="button-cancel-suspended">
              Cancel
            </Button>
            <Button className="flex-1 bg-brand-orange hover:bg-brand-orange/90" onClick={onContinue} data-testid="button-continue-suspended">
              {isSuspended ? "Continue Anyway + Get Help Reinstating" : "Start Verification"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BillingModal({
  open,
  onClose,
  onStartTrial,
  isPending,
}: {
  open: boolean;
  onClose: () => void;
  onStartTrial: () => void;
  isPending: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg p-0 overflow-hidden">
        <div className="flex">
          <div className="hidden md:flex w-40 bg-gradient-to-b from-brand-orange to-brand-blue items-center justify-center p-6">
            <div className="text-center space-y-2">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mx-auto">
                <span className="text-white font-bold text-xl">DD</span>
              </div>
              <p className="text-white/90 text-xs font-medium">Digital Domination</p>
            </div>
          </div>
          <div className="flex-1 p-6 space-y-4">
            <div>
              <h3 className="text-lg font-bold" data-testid="text-billing-title">Start your $1 trial</h3>
              <p className="text-sm text-muted-foreground mt-1">Your trial includes full access to:</p>
            </div>
            <div className="space-y-2.5">
              {[
                "Google Business Profile optimization to push you higher in the local Map Pack and make more money",
                "GeoGrid rankings that shows exactly where you outrank or lose to nearby competitors",
                "Listing protection alerts for suspensions, edits, and ranking drops before they cost you leads",
                "AI Maps SEO tools that tell you what to post, which keywords to add, and what to fix next on autopilot",
                "AI review engine that requests more 5‑star reviews and auto‑replies to every new one",
              ].map((feature) => (
                <div key={feature} className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
            <div className="p-3 rounded-xl bg-muted/30 border border-border/50">
              <p className="text-xs text-muted-foreground">
                $1 for your first 3 days, then as low as $399 every 28 days. Cancel anytime in 2 clicks.
              </p>
            </div>
            <div className="p-3 rounded-xl bg-brand-orange/5 border border-brand-orange/20">
              <div className="flex items-center gap-2 text-sm">
                <Shield className="w-4 h-4 text-brand-orange shrink-0" />
                <span className="text-muted-foreground">You'll be redirected to our secure checkout to confirm your card.</span>
              </div>
            </div>
            <div className="flex gap-3 pt-1">
              <Button variant="outline" className="flex-1" onClick={onClose} disabled={isPending}>
                Cancel
              </Button>
              <Button
                className="flex-1 bg-brand-orange hover:bg-brand-orange/90"
                onClick={onStartTrial}
                disabled={isPending}
                data-testid="button-start-trial"
              >
                {isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                ) : (
                  <CreditCard className="w-4 h-4 mr-1" />
                )}
                Start $1 trial
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StarRating({ rating }: { rating: number }) {
  const fullStars = Math.floor(rating);
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`w-3.5 h-3.5 ${
            i < fullStars ? "fill-amber-400 text-amber-400" : "text-gray-600"
          }`}
        />
      ))}
      {rating > 0 && <span className="text-xs text-muted-foreground ml-1">{rating.toFixed(1)}</span>}
    </div>
  );
}

function FunnelStep({
  label,
  value,
  color,
  widthPercent,
  icon: Icon,
}: {
  label: string;
  value: string;
  color: string;
  widthPercent: number;
  icon: any;
}) {
  return (
    <div className="flex items-center gap-3">
      <div
        className={`h-12 rounded-lg flex items-center justify-center gap-2 px-4 transition-all duration-500 ${color}`}
        style={{ width: `${widthPercent}%`, minWidth: "120px" }}
      >
        <Icon className="w-4 h-4 shrink-0" />
        <span className="text-sm font-semibold whitespace-nowrap">{value}</span>
      </div>
      <span className="text-xs text-muted-foreground whitespace-nowrap">{label}</span>
    </div>
  );
}

interface LoadingCheckItem {
  label: string;
  done: boolean;
}

function LoadingScreen({ onComplete, tokensAwarded }: { onComplete: () => void; tokensAwarded: boolean }) {
  const [items, setItems] = useState<LoadingCheckItem[]>([
    { label: "Setting up your dashboard", done: false },
    { label: "Importing your business data", done: false },
    { label: "Configuring rank tracking", done: false },
    { label: "Activating listing protection", done: false },
    { label: "Awarding your welcome tokens", done: false },
  ]);
  const [allDone, setAllDone] = useState(false);
  const completedRef = useRef(false);

  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];
    items.forEach((_, idx) => {
      timers.push(
        setTimeout(() => {
          setItems(prev => prev.map((item, i) => i <= idx ? { ...item, done: true } : item));
          if (idx === items.length - 1) {
            setTimeout(() => setAllDone(true), 600);
          }
        }, 800 * (idx + 1))
      );
    });
    return () => timers.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    if (allDone && !completedRef.current) {
      completedRef.current = true;
      setTimeout(onComplete, 1200);
    }
  }, [allDone, onComplete]);

  return (
    <Card className="border-brand-orange/20">
      <CardContent className="p-4 sm:p-8">
        <div className="flex flex-col items-center text-center space-y-6">
          <div className="w-16 h-16 rounded-2xl bg-brand-orange/10 flex items-center justify-center">
            <Rocket className="w-8 h-8 text-brand-orange" />
          </div>
          <div>
            <h2 className="text-xl font-bold" data-testid="text-loading-title">Preparing your dashboard...</h2>
            <p className="text-sm text-muted-foreground mt-1">This only takes a moment</p>
          </div>

          <div className="w-full max-w-sm space-y-3 text-left">
            {items.map((item, i) => (
              <div
                key={i}
                className={`flex items-center gap-3 p-3 rounded-xl transition-all duration-500 ${
                  item.done ? "bg-emerald-500/5 border border-emerald-500/20" : "bg-muted/30 border border-transparent"
                }`}
                data-testid={`loading-item-${i}`}
              >
                {item.done ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                ) : (
                  <Loader2 className="w-5 h-5 text-muted-foreground animate-spin shrink-0" />
                )}
                <span className={`text-sm ${item.done ? "text-foreground" : "text-muted-foreground"}`}>
                  {item.label}
                </span>
              </div>
            ))}
          </div>

          {allDone && (
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-brand-orange/10 border border-brand-orange/20 animate-in fade-in duration-500">
              <Trophy className="w-5 h-5 text-brand-orange" />
              <span className="text-sm font-semibold">+49 tokens earned!</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

const TOUR_STEPS = [
  { title: "Dashboard", description: "Your command center — see rankings, reviews, and performance at a glance.", route: "/dashboard", icon: "📊" },
  { title: "GeoGrid Tracking", description: "See exactly where you rank across your entire service area. Your scan data is here.", route: "/geogrid", icon: "🗺️" },
  { title: "GBP Optimization", description: "AI-powered tools to optimize your categories, descriptions, and service listings.", route: "/gbp-optimization", icon: "🎯" },
  { title: "Posts Engine", description: "Schedule and publish Google Business posts to keep your profile active.", route: "/posts", icon: "📝" },
  { title: "Reviews", description: "Monitor, respond to, and manage all your Google reviews in one place.", route: "/reviews", icon: "⭐" },
];

function GeoGridResultsStep({
  scanIds,
  onComplete,
}: {
  scanIds: Array<{ scanId: string; keyword: string }>;
  onComplete: (navigateTo?: string) => void;
}) {
  const [enrichmentTriggered, setEnrichmentTriggered] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [showVsl, setShowVsl] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  const { data: scanResults } = useQuery<any[]>({
    queryKey: ["/api/onboarding/scan-status", scanIds.map(s => s.scanId)],
    queryFn: async () => {
      const results = await Promise.all(
        scanIds.map(async ({ scanId, keyword }) => {
          try {
            const res = await fetch(`/api/geogrid-scans/scan/${scanId}`, { credentials: "include" });
            if (!res.ok) return { id: scanId, status: "pending", progress: 0, keywordName: keyword };
            const data = await res.json();
            return { ...data, keywordName: keyword };
          } catch {
            return { id: scanId, status: "pending", progress: 0, keywordName: keyword };
          }
        })
      );
      return results;
    },
    enabled: scanIds.length > 0,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 3000;
      const pending = data.filter((s: any) => s.status !== "completed" && s.status !== "failed");
      return pending.length > 0 ? 3000 : false;
    },
  });

  const completedScans = scanResults?.filter((s: any) => s.status === "completed") || [];
  const pendingScans = scanResults?.filter((s: any) => s.status !== "completed" && s.status !== "failed") || [];
  const allDone = scanResults && scanResults.length > 0 && pendingScans.length === 0;

  useEffect(() => {
    if (allDone && !enrichmentTriggered && completedScans.length > 0) {
      setEnrichmentTriggered(true);
      completedScans.forEach((scan: any) => {
        fetch(`/api/geogrid-scans/scan/${scan.id}/enrich-categories`, {
          method: "POST",
          credentials: "include",
        }).catch(() => {});
      });
    }
  }, [allDone, enrichmentTriggered, completedScans]);

  const handleStartTour = () => {
    setShowTour(true);
    setTourStep(0);
  };

  const handleTourNext = () => {
    if (tourStep < TOUR_STEPS.length - 1) {
      setTourStep(tourStep + 1);
    } else {
      onComplete(TOUR_STEPS[0].route);
    }
  };

  const handleTourNavigate = (route: string) => {
    onComplete(route);
  };

  return (
    <>
      <Dialog open={showVsl && scanIds.length > 0} onOpenChange={(open) => { if (!open) setShowVsl(false); }}>
        <DialogContent
          className="max-w-3xl p-0 overflow-hidden border-border/50 bg-[#0A0A0F] max-h-[90vh] overflow-y-auto"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <div className="p-5 space-y-4">
            {allDone ? (
              <>
                <div className="text-center space-y-2">
                  <div className="w-16 h-16 rounded-full bg-emerald-500/10 border-2 border-emerald-500/30 flex items-center justify-center mx-auto animate-in zoom-in duration-500">
                    <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                  </div>
                  <h3 className="text-lg font-bold text-white">Your scans are ready!</h3>
                  <p className="text-sm text-muted-foreground">All {scanIds.length} keyword{scanIds.length > 1 ? "s" : ""} scanned successfully</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {completedScans.map((scan: any, idx: number) => (
                    <div
                      key={scan.id || idx}
                      className="p-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5 animate-in fade-in slide-in-from-bottom-2 duration-500"
                      style={{ animationDelay: `${idx * 100}ms` }}
                      data-testid={`scan-done-card-${idx}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                        <span className="text-sm font-semibold text-white truncate">{scan.keywordName}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        {scan.avgRank && (
                          <div>
                            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Avg Rank</p>
                            <p className={`text-base font-bold ${scan.avgRank <= 3 ? "text-emerald-500" : scan.avgRank <= 10 ? "text-amber-500" : "text-red-500"}`}>
                              #{Math.round(scan.avgRank)}
                            </p>
                          </div>
                        )}
                        {scan.solvScore !== undefined && (
                          <div>
                            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">SoLV</p>
                            <p className="text-base font-bold text-brand-blue">{Math.round(scan.solvScore)}%</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center gap-2 p-3 rounded-xl bg-brand-orange/5 border border-brand-orange/20 animate-in fade-in duration-700">
                  <Trophy className="w-5 h-5 text-brand-orange shrink-0" />
                  <span className="text-sm font-semibold">+49 tokens earned! Your dashboard is ready.</span>
                </div>

                <div className="flex gap-3">
                  <Button
                    className="flex-1 bg-brand-orange hover:bg-brand-orange/90"
                    onClick={() => { setShowVsl(false); onComplete(); }}
                    data-testid="button-launch-from-dialog"
                  >
                    <Rocket className="w-4 h-4 mr-1.5" />
                    Start Your Journey
                  </Button>
                  <Button
                    variant="outline"
                    className="border-white/10 text-white/60 hover:bg-white/5"
                    onClick={() => setShowVsl(false)}
                    data-testid="button-close-vsl-done"
                  >
                    View Results
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="text-center space-y-1">
                  <h3 className="text-lg font-bold">While your scans are running...</h3>
                  <p className="text-sm text-muted-foreground">See how Digital Domination helps you dominate your local market</p>
                </div>
                <video
                  ref={videoRef}
                  src="https://s3.us-west-2.amazonaws.com/remotionlambda-uswest2-30tewi8y5c/renders/2ggc84h38g/output.mp4"
                  controls
                  autoPlay
                  muted
                  playsInline
                  className="w-full rounded-xl border border-[#1E1E2E]"
                  data-testid="vsl-video-onboarding"
                />
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-brand-blue" />
                    <span className="text-xs text-muted-foreground">
                      Scanning... {completedScans.length}/{scanIds.length} keywords complete
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowVsl(false)}
                    data-testid="button-close-vsl"
                  >
                    View Scan Progress
                  </Button>
                </div>

                <div className="border-t border-white/10 pt-4">
                  <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                    <Play className="w-4 h-4 text-[#0081DF]" />
                    See How It Works
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    {[
                      { vimeoId: "1122072569", title: "Digital Domination Overview" },
                      { vimeoId: "1122072587", title: "How Digital Domination Works" },
                      { vimeoId: "1122072611", title: "GeoGrid Scanning Explained" },
                      { vimeoId: "1122072802", title: "Maps Domination Results" },
                    ].map((v, i) => (
                      <a
                        key={i}
                        href={`https://player.vimeo.com/video/${v.vimeoId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-[#0081DF]/30 transition-colors overflow-hidden group"
                        data-testid={`onboarding-explainer-${i}`}
                      >
                        <div className="relative aspect-video bg-black/50">
                          <iframe
                            src={`https://player.vimeo.com/video/${v.vimeoId}?badge=0&autopause=0&player_id=0`}
                            className="w-full h-full pointer-events-none"
                            allow="autoplay; fullscreen"
                            title={v.title}
                          />
                          <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                            <div className="w-10 h-10 rounded-full bg-[#0081DF] flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                              <Play className="w-4 h-4 text-white fill-white ml-0.5" />
                            </div>
                          </div>
                        </div>
                        <div className="p-2.5">
                          <span className="text-xs font-bold text-white">{v.title}</span>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>

                <div className="border-t border-white/10 pt-4">
                  <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-brand-blue" />
                    Scan Progress
                  </h4>
                  <div className="space-y-2 mb-4">
                    {scanResults?.map((scan: any, idx: number) => (
                      <div
                        key={scan.id || idx}
                        className={`p-3 rounded-xl border transition-all ${
                          scan.status === "completed"
                            ? "border-emerald-500/20 bg-emerald-500/5"
                            : scan.status === "failed"
                            ? "border-red-500/20 bg-red-500/5"
                            : "border-white/[0.06] bg-white/[0.02]"
                        }`}
                        data-testid={`vsl-scan-card-${idx}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {scan.status === "completed" ? (
                              <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                            ) : scan.status === "failed" ? (
                              <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
                            ) : (
                              <Loader2 className="w-4 h-4 text-brand-blue animate-spin shrink-0" />
                            )}
                            <span className="text-sm font-semibold text-white">{scan.keywordName}</span>
                          </div>
                          {scan.status === "completed" && scan.avgRank && (
                            <div className="flex items-center gap-3">
                              <div className="text-right">
                                <p className="text-[10px] text-muted-foreground">Avg Rank</p>
                                <p className={`text-sm font-bold ${scan.avgRank <= 3 ? "text-emerald-500" : scan.avgRank <= 10 ? "text-amber-500" : "text-red-500"}`}>
                                  #{Math.round(scan.avgRank)}
                                </p>
                              </div>
                              {scan.solvScore !== undefined && (
                                <div className="text-right">
                                  <p className="text-[10px] text-muted-foreground">SoLV</p>
                                  <p className="text-sm font-bold text-brand-blue">{Math.round(scan.solvScore)}%</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        {scan.status !== "completed" && scan.status !== "failed" && (
                          <div className="mt-2">
                            <div className="h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
                              <div
                                className="h-full rounded-full bg-brand-blue transition-all duration-500"
                                style={{ width: `${Math.min((scan.progress || 10), 95)}%` }}
                              />
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-1">Scanning your service area...</p>
                          </div>
                        )}
                      </div>
                    )) || (
                      <div className="flex items-center justify-center gap-2 py-4">
                        <Loader2 className="w-4 h-4 animate-spin text-brand-blue" />
                        <span className="text-xs text-muted-foreground">Starting scans...</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="border-t border-white/10 pt-4">
                  <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                    <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                    Testimonials
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      {
                        name: "Bianca",
                        industry: "Bail Bonds, FL",
                        result: "200% Maps Visibility Surge",
                        vimeoId: "1143295236",
                        quote: "3X'd her Google Maps visibility in under 4 months, now expanding county by county.",
                      },
                      {
                        name: "Mark",
                        industry: "Eyeglasses, Pensacola",
                        result: "700% Keyword Growth",
                        vimeoId: "1140420130",
                        quote: "Map Pack monopolization in 7 weeks. From invisible to the Gulf Coast gorilla.",
                      },
                      {
                        name: "Josh",
                        industry: "Family Business",
                        result: "Top 3 in 8 Weeks",
                        vimeoId: "1139093793",
                        quote: "Went from not existing on Google Maps to selling his company to private equity.",
                      },
                      {
                        name: "Simply Health",
                        industry: "Medical Center",
                        result: "Daily Patient Growth",
                        vimeoId: "1143008329",
                        quote: "Turned unpredictable calls into consistent patient growth via Maps domination.",
                      },
                      {
                        name: "Medina Auto",
                        industry: "Car Dealership, 4 Locations",
                        result: "89 Unfair Reviews Deleted",
                        vimeoId: "1144736149",
                        quote: "Wiped out unfair 1-star reviews and took over Google Maps for every buyer search.",
                      },
                      {
                        name: "CA HVAC Co.",
                        industry: "HVAC, California",
                        result: "$746K Single Month",
                        vimeoId: "1143003880",
                        quote: "Hit $746,635 in a single month — a 223% year-over-year revenue surge.",
                      },
                    ].map((t, i) => (
                      <a
                        key={i}
                        href={`https://player.vimeo.com/video/${t.vimeoId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-[#F6642A]/30 transition-colors group"
                        data-testid={`onboarding-testimonial-${i}`}
                      >
                        <div className="w-10 h-10 rounded-lg bg-[#F6642A]/10 flex items-center justify-center shrink-0 group-hover:bg-[#F6642A]/20 transition-colors">
                          <Play className="w-4 h-4 text-[#F6642A] fill-[#F6642A]" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-xs font-bold text-white">{t.name}</span>
                            <span className="text-[10px] text-[#F6642A] font-semibold">{t.result}</span>
                          </div>
                          <p className="text-[11px] text-gray-400 line-clamp-2">{t.quote}</p>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

    <Card className="border-border/50">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-brand-blue" />
          Your GeoGrid Results
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {scanIds.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm text-muted-foreground">No scans were started. You can run scans from your dashboard later.</p>
          </div>
        ) : (
          <>
            {!allDone && (
              <div className="flex flex-col items-center justify-center py-4 gap-2">
                <div className="relative">
                  <div className="w-16 h-16 rounded-full border-4 border-brand-blue/20 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-brand-blue" />
                  </div>
                </div>
                <p className="text-sm font-medium mt-2">Scanning your service area...</p>
                <p className="text-xs text-muted-foreground">{completedScans.length} of {scanIds.length} keywords complete</p>
              </div>
            )}

            <div className="space-y-3">
              {scanResults?.map((scan: any, idx: number) => (
                <div
                  key={scan.id || idx}
                  className={`p-4 rounded-xl border transition-all ${
                    scan.status === "completed"
                      ? "border-emerald-500/20 bg-emerald-500/5"
                      : scan.status === "failed"
                      ? "border-red-500/20 bg-red-500/5"
                      : "border-border/50 bg-muted/20"
                  }`}
                  data-testid={`scan-result-${idx}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {scan.status === "completed" ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      ) : scan.status === "failed" ? (
                        <AlertTriangle className="w-4 h-4 text-red-500" />
                      ) : (
                        <Loader2 className="w-4 h-4 text-brand-blue animate-spin" />
                      )}
                      <span className="text-sm font-medium">{scan.keywordName}</span>
                    </div>
                    {scan.status === "completed" && scan.avgRank && (
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Avg Rank</p>
                          <p className={`text-sm font-bold ${scan.avgRank <= 3 ? "text-emerald-500" : scan.avgRank <= 10 ? "text-amber-500" : "text-red-500"}`}>
                            #{Math.round(scan.avgRank)}
                          </p>
                        </div>
                        {scan.solvScore !== undefined && (
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">SoLV</p>
                            <p className="text-sm font-bold text-brand-blue">{Math.round(scan.solvScore)}%</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {scan.status !== "completed" && scan.status !== "failed" && (
                    <div className="mt-2">
                      <div className="h-1.5 rounded-full bg-muted/50 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-brand-blue transition-all duration-500"
                          style={{ width: `${Math.min((scan.progress || 10), 95)}%` }}
                        />
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-1">Scanning your service area...</p>
                    </div>
                  )}
                </div>
              )) || (
                <div className="flex items-center justify-center gap-2 py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-brand-blue" />
                  <span className="text-sm text-muted-foreground">Starting your GeoGrid scans...</span>
                </div>
              )}
            </div>

            {allDone && completedScans.length > 0 && !showTour && (
              <div className="p-3 rounded-xl bg-brand-orange/5 border border-brand-orange/20">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-brand-orange shrink-0" />
                  <span className="text-sm">All scans complete! Let's take a quick look at what's been set up for you.</span>
                </div>
              </div>
            )}

            {showTour && (
              <div className="space-y-3 mt-2" data-testid="onboarding-tour">
                <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Quick Tour — {tourStep + 1} of {TOUR_STEPS.length}</p>
                <div className="space-y-2">
                  {TOUR_STEPS.map((ts, idx) => (
                    <button
                      key={ts.route}
                      onClick={() => handleTourNavigate(ts.route)}
                      className={`w-full text-left p-4 rounded-xl border transition-all ${
                        idx === tourStep
                          ? "border-brand-orange/40 bg-brand-orange/5 ring-1 ring-brand-orange/20"
                          : idx < tourStep
                          ? "border-emerald-500/20 bg-emerald-500/5 opacity-70"
                          : "border-border/30 bg-muted/10 opacity-50"
                      }`}
                      data-testid={`tour-step-${ts.route.replace("/", "")}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{ts.icon}</span>
                        <div>
                          <p className={`text-sm font-semibold ${idx === tourStep ? "text-foreground" : "text-muted-foreground"}`}>
                            {ts.title}
                            {idx < tourStep && <CheckCircle2 className="w-3 h-3 text-emerald-400 inline ml-2" />}
                          </p>
                          {idx === tourStep && (
                            <p className="text-xs text-muted-foreground mt-0.5">{ts.description}</p>
                          )}
                        </div>
                        {idx === tourStep && (
                          <ChevronRight className="w-4 h-4 text-brand-orange ml-auto shrink-0" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        <div className="flex items-center justify-end pt-2">
          {allDone || scanIds.length === 0 ? (
            showTour ? (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => onComplete()}
                  data-testid="button-skip-tour"
                >
                  Start Your Journey
                </Button>
                <Button
                  className="bg-brand-orange hover:bg-brand-orange/90"
                  onClick={handleTourNext}
                  data-testid="button-tour-next"
                >
                  {tourStep < TOUR_STEPS.length - 1 ? (
                    <>Next <ChevronRight className="w-4 h-4 ml-1" /></>
                  ) : (
                    <><Rocket className="w-4 h-4 mr-1" /> Start Your Journey</>
                  )}
                </Button>
              </div>
            ) : (
              <Button
                className="bg-brand-orange hover:bg-brand-orange/90"
                onClick={() => onComplete()}
                data-testid="button-launch-dashboard"
              >
                <Rocket className="w-4 h-4 mr-1" />
                Start Your Journey
              </Button>
            )
          ) : (
            <p className="text-xs text-muted-foreground">
              Waiting for scans to complete... ({completedScans.length}/{scanIds.length})
            </p>
          )}
        </div>
      </CardContent>
    </Card>
    </>
  );
}

export default function SetupPage() {
  const { setForceOnboarding } = useOnboarding();
  const savedStep = parseInt(sessionStorage.getItem("dd_setup_step") || "1");
  const [step, setStepRaw] = useState(savedStep > 7 ? 1 : savedStep);
  const setStep = useCallback((s: number) => {
    setStepRaw(s);
    sessionStorage.setItem("dd_setup_step", String(s));
    if (s > 1) {
      setForceOnboarding(true);
    }
  }, [setForceOnboarding]);
  const [selectedGbpLocations, setSelectedGbpLocations] = useState<Set<string>>(new Set());
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [leaderboardOptIn, setLeaderboardOptIn] = useState(true);
  const [showSuspendedDialog, setShowSuspendedDialog] = useState(false);
  const [suspendedLocation, setSuspendedLocation] = useState<GbpLocation | null>(null);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const passwordRef = useRef<HTMLInputElement>(null);
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { user, logout } = useAuth();
  const { setSelectedLocationId } = useLocationStore();

  const [aov, setAov] = useState("");
  const [bookRate, setBookRate] = useState(60);
  const [showRate, setShowRate] = useState(40);
  const [closeRate, setCloseRate] = useState(30);
  const [createdLocationId, setCreatedLocationIdRaw] = useState<string | null>(
    sessionStorage.getItem("dd_created_location_id")
  );
  const setCreatedLocationId = useCallback((id: string | null) => {
    setCreatedLocationIdRaw(id);
    if (id) {
      sessionStorage.setItem("dd_created_location_id", id);
    } else {
      sessionStorage.removeItem("dd_created_location_id");
    }
  }, []);
  const [tokensAwarded, setTokensAwarded] = useState(false);
  const [onboardingKeywords, setOnboardingKeywords] = useState<string[]>([]);
  const [scanIds, setScanIds] = useState<Array<{ scanId: string; keyword: string }>>([]);
  const [selectedLocationCategory, setSelectedLocationCategory] = useState("");
  const [selectedLocationCity, setSelectedLocationCity] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("google_connected") === "true") {
      window.history.replaceState({}, "", "/setup");
      toast({ title: "Google connected!", description: "Now select your business locations." });
      setStep(2);
    }
  }, []);

  const { data: googleStatus, isLoading: googleLoading } = useQuery<GoogleStatus>({
    queryKey: ["/api/google/status"],
  });

  const { data: gbpData, isLoading: gbpLoading, refetch: refetchGbp } = useQuery<GbpLocationsResponse>({
    queryKey: ["/api/google/locations"],
    enabled: !!googleStatus?.connected,
  });

  const connectMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("GET", "/api/google/auth-url?returnTo=/setup");
      const { authUrl } = await res.json();
      window.location.href = authUrl;
    },
    onError: (err: Error) => {
      toast({
        title: "Cannot connect",
        description: err.message.includes("503")
          ? "Google OAuth is not configured yet."
          : err.message,
        variant: "destructive",
      });
    },
  });

  const loginMutation = useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      const res = await apiRequest("POST", "/api/auth/login", { username: email, password });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      queryClient.invalidateQueries({ queryKey: ["/api/google/status"] });
      window.location.reload();
    },
    onError: () => {
      toast({
        title: "Login failed",
        description: "Invalid email or password. Please try again.",
        variant: "destructive",
      });
    },
  });

  useQuery<{ whopEnabled: boolean; hasDominationPlan: boolean }>({
    queryKey: ["/api/billing/config"],
  });

  const trialMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/billing/create-trial", {});
      return (await res.json()) as { url: string | null };
    },
  });

  const setupMutation = useMutation({
    mutationFn: async (locations: Array<{ name: string; city: string; category: string; address: string; state: string; zip: string; rating: number; reviews: number; gbpStatus: string; phone?: string; website?: string; gbpPlaceId?: string; gbpLocationId?: string; gbpAccountId?: string; lat?: number; lng?: number }>) => {
      const payload = locations.map(loc => ({
        name: loc.name,
        city: loc.city || "Local",
        category: loc.category || "Business",
        address: loc.address,
        state: loc.state,
        zip: loc.zip,
        phone: loc.phone || "",
        website: loc.website || "",
        gbpPlaceId: loc.gbpPlaceId || "",
        gbpLocationId: loc.gbpLocationId || "",
        gbpAccountId: loc.gbpAccountId || "",
        lat: loc.lat || 0,
        lng: loc.lng || 0,
        leaderboardOptIn: false,
        gbpStatus: loc.gbpStatus as "active" | "suspended" | "unverified",
      }));
      let result: any;
      if (payload.length === 1) {
        const res = await apiRequest("POST", "/api/locations/setup", payload[0]);
        result = await res.json();
      } else {
        const res = await apiRequest("POST", "/api/locations/setup", { locations: payload });
        result = await res.json();
      }
      return result;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/locations"] });
      const locId = Array.isArray(data) ? data[0]?.id : data?.id;
      if (locId) setCreatedLocationId(locId);
      setStep(3);
    },
    onError: (err: Error) => {
      // Server returns "402: {json}" for tier-cap violations. Detect and
      // redirect the user to /pricing so they can upgrade.
      const msg = err.message || "";
      if (msg.startsWith("402")) {
        try {
          const jsonStart = msg.indexOf("{");
          const payload = jsonStart >= 0 ? JSON.parse(msg.slice(jsonStart)) : null;
          toast({
            title: "Location limit reached",
            description: payload?.message || "Upgrade your plan to add more locations.",
            variant: "destructive",
          });
          setLocation(payload?.upgradeUrl || "/pricing");
          return;
        } catch {
          setLocation("/pricing");
          return;
        }
      }
      toast({
        title: "Setup failed",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const updateLocationMutation = useMutation({
    mutationFn: async (data: { locationId: string; updates: Record<string, any> }) => {
      await apiRequest("PATCH", `/api/locations/${data.locationId}`, data.updates);
    },
  });

  const onboardingCompleteMutation = useMutation({
    mutationFn: async (locationId: string) => {
      const res = await apiRequest("POST", "/api/onboarding/complete", {
        locationId,
        completedSteps: ["google_connected", "location_selected", "profile_verified", "revenue_set", "keywords_set", "leaderboard_set", "onboarding_complete"],
      });
      return res.json();
    },
    onSuccess: () => {
      setTokensAwarded(true);
    },
  });

  const { data: profileData, isLoading: profileLoading } = useQuery<{ fields: ProfileField[]; score: number; error?: string }>({
    queryKey: ["/api/gbp/profile-completeness", createdLocationId],
    queryFn: async () => {
      const res = await fetch(`/api/gbp/profile-completeness/${createdLocationId}`, { credentials: "include" });
      if (!res.ok) return { fields: [], score: 0, error: "fetch_failed" };
      return res.json();
    },
    enabled: !!createdLocationId && step === 3,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data || data.error || data.fields.length === 0) return 3000;
      return false;
    },
  });

  useEffect(() => {
    if (profileData?.fields) {
      const catField = profileData.fields.find(f => f.key === "primaryCategory");
      if (catField?.value && !selectedLocationCategory) {
        setSelectedLocationCategory(catField.value);
      }
    }
  }, [profileData]);

  const suggestKeywordsMutation = useMutation({
    mutationFn: async ({ category, city }: { category: string; city: string }) => {
      const res = await apiRequest("POST", "/api/onboarding/suggest-keywords", { category, city });
      return res.json();
    },
    onSuccess: (data: { keywords: string[] }) => {
      setOnboardingKeywords(data.keywords);
    },
    onError: () => {
      setOnboardingKeywords([
        `${selectedLocationCategory || "business"} ${selectedLocationCity || "near me"}`,
        `best ${selectedLocationCategory || "business"} ${selectedLocationCity || "near me"}`,
        `${selectedLocationCategory || "business"} reviews`,
      ]);
    },
  });

  const runScansMutation = useMutation({
    mutationFn: async ({ locationId, keywords }: { locationId: string; keywords: string[] }) => {
      const res = await apiRequest("POST", "/api/onboarding/run-scans", { locationId, keywords });
      return res.json();
    },
    onSuccess: (data: { scans: Array<{ scanId: string; keyword: string }> }) => {
      setScanIds(data.scans.map(s => ({ scanId: s.scanId, keyword: s.keyword })));
    },
    onError: (err: Error) => {
      toast({
        title: "Scan setup note",
        description: "GeoGrid scans couldn't be started now. You can run them from the GeoGrid page later.",
      });
    },
  });

  const googleConnected = googleStatus?.connected;
  const gbpLocations = gbpData?.locations || [];
  const filteredGbpLocations = gbpLocations.filter((loc) =>
    loc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    loc.address.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleGbpSelection = (resourceName: string) => {
    const loc = gbpLocations.find((l) => l.resourceName === resourceName);
    if (loc && (loc.verificationState === "SUSPENDED" || loc.gbpStatus === "disabled")) {
      setSuspendedLocation(loc);
      setShowSuspendedDialog(true);
      return;
    }
    if (loc && loc.verificationState === "UNVERIFIED") {
      setSuspendedLocation(loc);
      setShowSuspendedDialog(true);
      return;
    }
    setSelectedGbpLocations((prev) => {
      if (prev.has(resourceName)) {
        return new Set();
      }
      return new Set([resourceName]);
    });
  };

  const handleSuspendedContinue = () => {
    if (suspendedLocation) {
      setSelectedGbpLocations(new Set([suspendedLocation.resourceName]));
    }
    setShowSuspendedDialog(false);
    setSuspendedLocation(null);
  };

  const handleProceedToBilling = () => {
    // Save the selected location(s) immediately. The server enforces tier
    // location caps and returns 402 with upgradeUrl when over the limit —
    // setupMutation.onError will redirect to /pricing in that case.
    const selectedLocs = gbpLocations
      .filter((l) => selectedGbpLocations.has(l.resourceName))
      .map((l) => ({
        name: l.title,
        city: l.city,
        category: l.primaryCategory || "Business",
        address: l.address,
        state: l.state,
        zip: l.zip,
        rating: l.rating,
        reviews: l.totalReviews,
        gbpStatus: l.verificationState === "SUSPENDED" ? "suspended" : l.verificationState === "UNVERIFIED" ? "unverified" : "active",
        phone: l.phone || "",
        website: l.website || "",
        gbpPlaceId: l.placeId || "",
        gbpLocationId: l.resourceName || "",
        gbpAccountId: l.accountName || "",
        lat: l.lat || 0,
        lng: l.lng || 0,
      }));

    if (selectedLocs.length > 0) {
      setSelectedLocationCity(selectedLocs[0].city);
      if (selectedLocs[0].category && selectedLocs[0].category !== "Business") {
        setSelectedLocationCategory(selectedLocs[0].category);
      }
    }

    setupMutation.mutate(selectedLocs);
  };

  const handleStartTrial = async () => {
    // Legacy upsell modal — no longer shown. Kept so the BillingDialog prop
    // contract still resolves. Sends user to /pricing if it ever does open.
    setShowBillingModal(false);
    setLocation("/pricing");
    return;
    const result = await trialMutation.mutateAsync();
    if (result?.url) {
      window.location.href = result.url;
      return;
    }

    const selectedLocs = gbpLocations
      .filter((l) => selectedGbpLocations.has(l.resourceName))
      .map((l) => ({
        name: l.title,
        city: l.city,
        category: l.primaryCategory || "Business",
        address: l.address,
        state: l.state,
        zip: l.zip,
        rating: l.rating,
        reviews: l.totalReviews,
        gbpStatus: l.verificationState === "SUSPENDED" ? "suspended" : l.verificationState === "UNVERIFIED" ? "unverified" : "active",
        phone: l.phone || "",
        website: l.website || "",
        gbpPlaceId: l.placeId || "",
        gbpLocationId: l.resourceName || "",
        gbpAccountId: l.accountName || "",
        lat: l.lat || 0,
        lng: l.lng || 0,
      }));

    if (selectedLocs.length > 0) {
      setSelectedLocationCity(selectedLocs[0].city);
      if (selectedLocs[0].category && selectedLocs[0].category !== "Business") {
        setSelectedLocationCategory(selectedLocs[0].category);
      }
    }

    setupMutation.mutate(selectedLocs);
  };

  const handleSaveRevenue = async () => {
    try {
      if (createdLocationId) {
        const updates: Record<string, any> = {};
        if (aov) updates.aov = aov;
        updates.bookRate = String(bookRate);
        updates.showRate = String(showRate);
        updates.closeRate = String(closeRate);
        await updateLocationMutation.mutateAsync({ locationId: createdLocationId, updates });
      }

      if (selectedLocationCategory || selectedLocationCity) {
        suggestKeywordsMutation.mutate({
          category: selectedLocationCategory || "Business",
          city: selectedLocationCity || "Local",
        });
      }

      setStep(5);
    } catch {
      toast({ title: "Failed to save", description: "Please try again.", variant: "destructive" });
    }
  };

  const handleSkipRevenue = () => {
    if (selectedLocationCategory || selectedLocationCity) {
      suggestKeywordsMutation.mutate({
        category: selectedLocationCategory || "Business",
        city: selectedLocationCity || "Local",
      });
    }
    setStep(5);
  };

  const handleRunScans = async () => {
    const selectedKeywords = onboardingKeywords.filter(k => k.trim());
    if (createdLocationId && selectedKeywords.length > 0) {
      runScansMutation.mutate({ locationId: createdLocationId, keywords: selectedKeywords });
    }
    setStep(7);
  };

  const handleSaveLeaderboard = async () => {
    try {
      await apiRequest("PATCH", "/api/account", { leaderboardOptIn });
    } catch (e) {
      console.error("Failed to save leaderboard opt-in:", e);
    }

    const selectedKeywords = onboardingKeywords.filter(k => k.trim());
    if (createdLocationId && selectedKeywords.length > 0) {
      runScansMutation.mutate({ locationId: createdLocationId, keywords: selectedKeywords });
    }

    if (createdLocationId) {
      onboardingCompleteMutation.mutate(createdLocationId);
    }
    setStep(7);
  };

  const handleLoadingComplete = (_navigateTo?: string) => {
    sessionStorage.removeItem("dd_setup_step");
    sessionStorage.removeItem("dd_created_location_id");
    setForceOnboarding(false);
    if (createdLocationId) {
      setSelectedLocationId(createdLocationId);
    }
    toast({
      title: "Welcome to Digital Domination!",
      description: "Your dashboard is ready. You've earned 49 tokens!",
    });
    setLocation("/road-to-domination");
  };

  const addKeyword = () => {
    if (onboardingKeywords.length < 5) setOnboardingKeywords([...onboardingKeywords, ""]);
  };
  const removeKeyword = (idx: number) => {
    setOnboardingKeywords(onboardingKeywords.filter((_, i) => i !== idx));
  };
  const updateKeyword = (idx: number, value: string) => {
    setOnboardingKeywords(onboardingKeywords.map((k, i) => i === idx ? value : k));
  };

  const stepLabels = [
    { icon: SiGoogle, label: "Google" },
    { icon: MapPin, label: "Location" },
    { icon: ListChecks, label: "Verify" },
    { icon: DollarSign, label: "Revenue" },
    { icon: Target, label: "Keywords" },
    { icon: Trophy, label: "Leaderboard" },
    { icon: Rocket, label: "Launch" },
  ];

  const stepDescriptions: Record<number, string> = {
    1: "For 5,000+ businesses we’ve worked with, Google delivers more leads than any other channel — when it’s set up and managed right. That’s exactly what we’re about to do for you.",
    2: "Select the business you want to set up first",
    3: "Let’s verify your Google Business Profile is accurate",
    4: "Help us estimate your revenue from Google leads",
    5: "We’ll run free GeoGrid scans on your top keywords",
    6: "See how you stack up against other businesses",
    7: "Your GeoGrid scans are running. Here’s what we found so far.",
  };

  return (
    <div className="h-screen flex items-start justify-center pt-8 pb-16 px-6 bg-gradient-to-br from-background via-background to-primary/5 relative overflow-y-auto">
      <div className="fixed top-4 right-4 z-50 flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:text-foreground gap-2"
          onClick={() => {
            sessionStorage.removeItem("dd_setup_step");
            sessionStorage.removeItem("dd_created_location_id");
            setForceOnboarding(false);
            setLocation("/dashboard");
          }}
          data-testid="button-setup-exit-dashboard"
        >
          <ChevronRight className="w-4 h-4 rotate-180" />
          Exit to Dashboard
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:text-foreground gap-2"
          onClick={() => logout.mutate()}
          data-testid="button-setup-logout"
        >
          <LogOut className="w-4 h-4" />
          Log out
        </Button>
      </div>
      <div className="w-full max-w-xl space-y-6">
        {step > 1 && (
          <>
            <div className="text-center space-y-2">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-7 h-7 text-primary" />
              </div>
              <h1 className="text-3xl font-bold tracking-tight" data-testid="text-setup-title">
                {step === 7 ? "Welcome to the team. 5,001 businesses and counting." : "Let’s turn your Google listing into your #1 lead source."}
              </h1>
              <p className="text-muted-foreground">{stepDescriptions[step]}</p>
            </div>

            <div className="flex items-center justify-center gap-1 flex-wrap">
              {stepLabels.map(({ icon: Icon, label }, i) => (
                <div key={i} className="flex items-center gap-1">
                  <div className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium transition-all ${
                    step > i + 1
                      ? "bg-emerald-500/10 text-emerald-500"
                      : step === i + 1
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground"
                  }`} data-testid={`step-indicator-${i + 1}`}>
                    {step > i + 1 ? (
                      <Check className="w-3.5 h-3.5" />
                    ) : (
                      <Icon className="w-3.5 h-3.5" />
                    )}
                    <span className="hidden sm:inline">{label}</span>
                  </div>
                  {i < stepLabels.length - 1 && (
                    <ChevronRight className="w-3 h-3 text-muted-foreground/50" />
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {step === 1 && (
          <Card className="border-border/50">
            <CardHeader className="text-center pb-2">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
                <Sparkles className="w-7 h-7 text-primary" />
              </div>
              <CardTitle className="text-xl">Connect Your Account</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5 pt-2">
              {googleLoading ? (
                <div className="flex items-center justify-center gap-2 py-6">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm text-muted-foreground">Checking connection...</span>
                </div>
              ) : googleConnected ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    <div>
                      <p className="text-sm font-medium text-emerald-500">Google Connected</p>
                      {googleStatus?.googleEmail && (
                        <p className="text-xs text-muted-foreground">{googleStatus.googleEmail}</p>
                      )}
                    </div>
                  </div>
                  <Button
                    className="w-full bg-brand-orange hover:bg-brand-orange/90"
                    onClick={() => setStep(2)}
                    data-testid="button-next-to-picker"
                  >
                    Continue to Location Picker
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <Button
                    className="w-full h-12 bg-brand-orange hover:bg-brand-orange/90 text-base font-medium gap-3"
                    onClick={() => connectMutation.mutate()}
                    disabled={connectMutation.isPending || !googleStatus?.configured}
                    data-testid="button-connect-google"
                  >
                    {connectMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <SiGoogle className="w-4 h-4" />
                    )}
                    Sign in with Google
                  </Button>

                  {!googleStatus?.configured && (
                    <p className="text-xs text-muted-foreground text-center">
                      Google OAuth not configured. Contact your administrator.
                    </p>
                  )}

                  <div className="relative flex items-center">
                    <div className="flex-grow border-t border-border/50" />
                    <span className="flex-shrink mx-4 text-xs text-muted-foreground uppercase tracking-wider">or</span>
                    <div className="flex-grow border-t border-border/50" />
                  </div>

                  <form
                    className="space-y-3"
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (!showPasswordField) {
                        if (emailInput.trim()) {
                          setShowPasswordField(true);
                          setTimeout(() => passwordRef.current?.focus(), 50);
                        }
                      } else {
                        if (emailInput.trim() && passwordInput.trim()) {
                          loginMutation.mutate({ email: emailInput.trim(), password: passwordInput });
                        }
                      }
                    }}
                  >
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="email"
                        placeholder="Enter your email"
                        value={emailInput}
                        onChange={(e) => {
                          setEmailInput(e.target.value);
                          if (!e.target.value.trim()) {
                            setShowPasswordField(false);
                            setPasswordInput("");
                          }
                        }}
                        className="pl-10 h-12 text-base"
                        data-testid="input-setup-email"
                      />
                    </div>

                    {showPasswordField && (
                      <div className="space-y-3 animate-in slide-in-from-top-2 duration-200">
                        <div className="relative">
                          <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            ref={passwordRef}
                            type="password"
                            placeholder="Enter your password"
                            value={passwordInput}
                            onChange={(e) => setPasswordInput(e.target.value)}
                            className="pl-10 h-12 text-base"
                            data-testid="input-setup-password"
                          />
                        </div>
                        {loginMutation.isError && (
                          <p className="text-sm text-destructive" data-testid="text-setup-login-error">
                            Invalid email or password
                          </p>
                        )}
                        <Button
                          type="submit"
                          className="w-full h-12 text-base font-medium"
                          disabled={loginMutation.isPending || !passwordInput.trim()}
                          data-testid="button-setup-login"
                        >
                          {loginMutation.isPending ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : null}
                          Log In
                        </Button>
                      </div>
                    )}
                  </form>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {step === 2 && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Select a Business Location</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => refetchGbp()}>
                  <RefreshCw className="w-3 h-3 mr-1" />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search locations..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") setSearchQuery(searchInput); }}
                    className="pl-10"
                    data-testid="input-search-locations"
                  />
                </div>
                <Button
                  size="sm"
                  className="bg-brand-orange hover:bg-brand-orange/90 px-4 h-10"
                  onClick={() => setSearchQuery(searchInput)}
                  data-testid="button-search-locations"
                >
                  Search
                </Button>
              </div>

              {gbpData?.needsGbpScope && (
                <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/20 space-y-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-500">Business Profile access needed</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Your Google account is connected, but we need additional permission to access your Business Profile locations.
                      </p>
                    </div>
                  </div>
                  <Button
                    className="w-full bg-brand-orange hover:bg-brand-orange/90"
                    onClick={() => connectMutation.mutate()}
                    disabled={connectMutation.isPending}
                    data-testid="button-connect-gbp-scope"
                  >
                    {connectMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <SiGoogle className="w-4 h-4 mr-2" />
                    )}
                    Connect Google Business Profile
                  </Button>
                </div>
              )}

              {gbpData?.needsReconnect && !gbpData?.needsGbpScope && (
                <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/20 space-y-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-500">Reconnect Google Business Profile</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Your Google connection has expired or is missing permissions. Reconnect to load your business locations.
                      </p>
                    </div>
                  </div>
                  <Button
                    className="w-full bg-brand-orange hover:bg-brand-orange/90"
                    onClick={() => connectMutation.mutate()}
                    disabled={connectMutation.isPending}
                    data-testid="button-reconnect-gbp"
                  >
                    {connectMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <SiGoogle className="w-4 h-4 mr-2" />
                    )}
                    Reconnect Google
                  </Button>
                </div>
              )}

              <div className="space-y-2 max-h-72 overflow-y-auto">
                {gbpLoading ? (
                  <div
                    className="flex flex-col items-center justify-center py-10 gap-4"
                    data-testid="loading-gbp-locations"
                  >
                    <div className="relative">
                      <Loader2 className="w-10 h-10 text-brand-orange animate-spin" />
                      <SiGoogle className="w-4 h-4 absolute inset-0 m-auto text-brand-orange" />
                    </div>
                    <div className="text-center space-y-1">
                      <p className="text-sm font-medium">Loading your Google Business Profile locations…</p>
                      <p className="text-xs text-muted-foreground">
                        This can take 10–30 seconds while Google returns your account data.
                      </p>
                    </div>
                    <div className="w-full max-w-xs h-1.5 rounded-full bg-muted overflow-hidden">
                      <div className="h-full w-1/3 bg-brand-orange rounded-full animate-[loading-bar_1.4s_ease-in-out_infinite]" />
                    </div>
                  </div>
                ) : gbpData?.needsGbpScope || gbpData?.needsReconnect ? null : filteredGbpLocations.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">No locations found. Make sure your Google account has Business Profile locations.</p>
                ) : (
                  filteredGbpLocations.map((loc) => {
                    const selected = selectedGbpLocations.has(loc.resourceName);
                    const statusBadge = (() => {
                      if (loc.verificationState === "SUSPENDED" || loc.gbpStatus === "suspended") {
                        return <Badge variant="destructive" className="text-[10px] shrink-0">Suspended</Badge>;
                      }
                      if (loc.gbpStatus === "disabled") {
                        return <Badge variant="destructive" className="text-[10px] shrink-0">Disabled</Badge>;
                      }
                      if (loc.gbpStatus === "temporarily_closed") {
                        return <Badge className="text-[10px] shrink-0 bg-amber-500/15 text-amber-500 border-amber-500/30 hover:bg-amber-500/20">Temp Closed</Badge>;
                      }
                      if (loc.gbpStatus === "needs_review") {
                        return <Badge className="text-[10px] shrink-0 bg-amber-500/15 text-amber-500 border-amber-500/30 hover:bg-amber-500/20">Needs Review</Badge>;
                      }
                      if (loc.verificationState === "UNVERIFIED" || loc.verificationState === "UNKNOWN") {
                        return <Badge variant="outline" className="text-[10px] shrink-0">Unverified</Badge>;
                      }
                      if (loc.verificationState === "PENDING") {
                        return <Badge className="text-[10px] shrink-0 bg-blue-500/15 text-blue-400 border-blue-500/30 hover:bg-blue-500/20">Pending</Badge>;
                      }
                      if (loc.verificationState === "VERIFIED") {
                        return <Badge className="text-[10px] shrink-0 bg-emerald-500/15 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/20">Verified</Badge>;
                      }
                      return null;
                    })();
                    return (
                      <div
                        key={loc.resourceName}
                        className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                          selected
                            ? "border-primary bg-primary/5"
                            : "border-border/50 hover:bg-muted/30"
                        }`}
                        onClick={() => toggleGbpSelection(loc.resourceName)}
                        data-testid={`card-gbp-location-${loc.resourceName}`}
                      >
                        <div className={`mt-1 w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${selected ? "border-primary bg-primary" : "border-muted-foreground/40"}`}>
                          {selected && <div className="w-2 h-2 rounded-full bg-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium truncate">{loc.title}</p>
                            {statusBadge}
                          </div>
                          <p className="text-xs text-muted-foreground truncate mt-0.5">
                            {[loc.address, loc.city, loc.state].filter(Boolean).join(", ")}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            <StarRating rating={loc.rating} />
                            {loc.primaryCategory && (
                              <span className="text-[10px] text-muted-foreground">· {loc.primaryCategory}</span>
                            )}
                            {loc.phone && (
                              <span className="text-[10px] text-muted-foreground">· {loc.phone}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="flex items-center justify-between border-t border-border/50 pt-4">
                <Button variant="ghost" onClick={() => setStep(1)} data-testid="button-back-to-connect">
                  Back
                </Button>
                <Button
                  className="bg-brand-orange hover:bg-brand-orange/90"
                  disabled={selectedGbpLocations.size === 0 || setupMutation.isPending}
                  onClick={handleProceedToBilling}
                  data-testid="button-proceed-billing"
                >
                  {setupMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  ) : (
                    <ChevronRight className="w-4 h-4 mr-1" />
                  )}
                  Continue
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 3 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <ListChecks className="w-5 h-5 text-brand-blue" />
                Verify Your Profile
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                We pulled your profile data from Google. Please review and confirm everything looks correct.
              </p>

              {profileData && profileData.fields && profileData.fields.length > 0 ? (
                <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                  {profileData.fields.map((field) => {
                    const iconMap: Record<string, any> = {
                      title: Building2,
                      primaryCategory: Tag,
                      additionalCategories: Tag,
                      phone: Phone,
                      website: Globe,
                      address: MapPin,
                      regularHours: Clock,
                      specialHours: CalendarDays,
                      openingDate: CalendarDays,
                      description: Pencil,
                      serviceItems: ListChecks,
                      serviceArea: MapPin,
                    };
                    const Icon = iconMap[field.key] || Eye;

                    return (
                      <div
                        key={field.key}
                        className={`flex items-center gap-3 p-3 rounded-xl border ${
                          field.status === "complete"
                            ? "border-emerald-500/20 bg-emerald-500/5"
                            : field.status === "partial"
                            ? "border-amber-500/20 bg-amber-500/5"
                            : "border-red-500/20 bg-red-500/5"
                        }`}
                        data-testid={`profile-field-${field.key}`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                          field.status === "complete" ? "bg-emerald-500/10" : field.status === "partial" ? "bg-amber-500/10" : "bg-red-500/10"
                        }`}>
                          <Icon className={`w-4 h-4 ${
                            field.status === "complete" ? "text-emerald-500" : field.status === "partial" ? "text-amber-500" : "text-red-500"
                          }`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{field.label}</p>
                          {field.value ? (
                            <p className="text-xs text-muted-foreground truncate">{field.value}</p>
                          ) : (
                            <p className="text-xs text-red-400">Not set</p>
                          )}
                        </div>
                        {field.status === "complete" ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                        ) : field.status === "partial" ? (
                          <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                        ) : (
                          <X className="w-4 h-4 text-red-400 shrink-0" />
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex flex-col items-center justify-center py-8 gap-3">
                    <Loader2 className="w-8 h-8 animate-spin text-brand-blue" />
                    <p className="text-sm font-medium">Syncing your Google Business Profile...</p>
                    <p className="text-xs text-muted-foreground">This usually takes 10-20 seconds</p>
                  </div>
                  <div className="space-y-2">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <Skeleton key={i} className="h-12" />
                    ))}
                  </div>
                </div>
              )}

              {profileData && profileData.fields.some(f => f.status !== "complete") && (
                <div className="p-3 rounded-xl bg-amber-500/5 border border-amber-500/20">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-xs text-muted-foreground">
                      Some fields are incomplete. You can update them in your Google Business Profile and we'll track them on the Profile Essentials page.
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between pt-2">
                <Button variant="ghost" onClick={() => setStep(2)} data-testid="button-back-step3">
                  Back
                </Button>
                <Button
                  className="bg-brand-orange hover:bg-brand-orange/90"
                  onClick={() => setStep(4)}
                  data-testid="button-continue-step3"
                >
                  Track & Continue
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 4 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-emerald-500" />
                Revenue Hero
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <p className="text-sm text-muted-foreground">
                Tell us about your sales funnel so we can estimate revenue from your Google leads.
              </p>

              <div className="space-y-4">
                <div className="space-y-3 p-4 rounded-xl bg-muted/20 border border-border/50">
                  <h4 className="text-sm font-semibold flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-brand-blue" />
                    Your Lead Funnel
                  </h4>

                  <div className="space-y-4">
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs flex items-center gap-1.5">
                          <Phone className="w-3 h-3" />
                          Book Rate
                        </Label>
                        <span className="text-sm font-bold text-brand-blue">{bookRate}%</span>
                      </div>
                      <Slider
                        value={[bookRate]}
                        onValueChange={([v]) => setBookRate(v)}
                        min={10}
                        max={100}
                        step={5}
                        className="w-full"
                        data-testid="slider-book-rate"
                      />
                      <p className="text-[11px] text-muted-foreground">Of incoming calls, what % book an appointment?</p>
                    </div>

                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs flex items-center gap-1.5">
                          <Users className="w-3 h-3" />
                          Show Rate
                        </Label>
                        <span className="text-sm font-bold text-brand-blue">{showRate}%</span>
                      </div>
                      <Slider
                        value={[showRate]}
                        onValueChange={([v]) => setShowRate(v)}
                        min={10}
                        max={100}
                        step={5}
                        className="w-full"
                        data-testid="slider-show-rate"
                      />
                      <p className="text-[11px] text-muted-foreground">Of booked appointments, what % actually show up?</p>
                    </div>

                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs flex items-center gap-1.5">
                          <Target className="w-3 h-3" />
                          Close Rate
                        </Label>
                        <span className="text-sm font-bold text-brand-blue">{closeRate}%</span>
                      </div>
                      <Slider
                        value={[closeRate]}
                        onValueChange={([v]) => setCloseRate(v)}
                        min={10}
                        max={100}
                        step={5}
                        className="w-full"
                        data-testid="slider-close-rate"
                      />
                      <p className="text-[11px] text-muted-foreground">Of those who show, what % become paying customers?</p>
                    </div>
                  </div>

                  <div className="pt-3 border-t border-border/30">
                    <div className="space-y-1">
                      <FunnelStep label="Incoming Calls" value="100 calls" color="bg-brand-blue/20 text-brand-blue" widthPercent={100} icon={Phone} />
                      <div className="flex justify-center"><ArrowDown className="w-4 h-4 text-muted-foreground/50" /></div>
                      <FunnelStep label="Booked" value={`${bookRate} booked`} color="bg-indigo-500/20 text-indigo-400" widthPercent={bookRate} icon={CalendarDays} />
                      <div className="flex justify-center"><ArrowDown className="w-4 h-4 text-muted-foreground/50" /></div>
                      <FunnelStep label="Showed" value={`${Math.round(bookRate * showRate / 100)} showed`} color="bg-amber-500/20 text-amber-400" widthPercent={bookRate * showRate / 100} icon={Users} />
                      <div className="flex justify-center"><ArrowDown className="w-4 h-4 text-muted-foreground/50" /></div>
                      <FunnelStep label="Closed" value={`${Math.round(bookRate * showRate * closeRate / 10000)} closed`} color="bg-emerald-500/20 text-emerald-400" widthPercent={bookRate * showRate * closeRate / 10000} icon={CheckCircle2} />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm flex items-center gap-1.5">
                    <DollarSign className="w-4 h-4 text-emerald-500" />
                    Average Order Value
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    What's the average dollar amount of a single sale or job?
                  </p>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="number"
                      placeholder="e.g. 250"
                      value={aov}
                      onChange={(e) => setAov(e.target.value)}
                      className="pl-10 text-lg"
                      data-testid="input-aov"
                    />
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {["100", "250", "500", "1000"].map(val => (
                      <Button
                        key={val}
                        variant={aov === val ? "default" : "outline"}
                        size="sm"
                        onClick={() => setAov(val)}
                        data-testid={`button-aov-${val}`}
                      >
                        ${val}
                      </Button>
                    ))}
                  </div>
                </div>

                {aov && (
                  <div className="p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-emerald-500 shrink-0" />
                      <span className="text-sm">
                        Per 100 calls: ~<span className="font-bold text-emerald-400">
                          ${(Math.round(bookRate * showRate * closeRate / 10000) * Number(aov)).toLocaleString()}
                        </span> estimated revenue
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
                <Button variant="ghost" onClick={() => setStep(3)} data-testid="button-back-step4">
                  Back
                </Button>
                <div className="flex items-center gap-2 ml-auto">
                  <Button
                    variant="ghost"
                    onClick={handleSkipRevenue}
                    disabled={updateLocationMutation.isPending}
                    data-testid="button-skip-step4"
                    className="text-muted-foreground"
                  >
                    Skip for now
                  </Button>
                  <Button
                    className="bg-brand-orange hover:bg-brand-orange/90"
                    onClick={handleSaveRevenue}
                    disabled={updateLocationMutation.isPending}
                    data-testid="button-continue-step4"
                  >
                    {updateLocationMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-1" />
                    ) : null}
                    Continue
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground text-center -mt-2">
                Don't know these numbers? Skip for now — you can add them later from Settings.
              </p>
            </CardContent>
          </Card>
        )}

        {step === 5 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Target className="w-5 h-5 text-brand-orange" />
                Keyword Ideas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                We recommend these keywords based on your business category. Edit, remove, or add your own — we'll run a free GeoGrid scan for each one.
              </p>

              {suggestKeywordsMutation.isPending ? (
                <div className="flex items-center justify-center gap-2 py-6">
                  <Loader2 className="w-5 h-5 animate-spin text-brand-orange" />
                  <span className="text-sm text-muted-foreground">Generating keyword ideas...</span>
                </div>
              ) : (
                <div className="space-y-2">
                  {onboardingKeywords.map((kw, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-brand-orange/10 flex items-center justify-center shrink-0">
                        <span className="text-xs font-bold text-brand-orange">{idx + 1}</span>
                      </div>
                      <Input
                        placeholder={`Keyword ${idx + 1}`}
                        value={kw}
                        onChange={(e) => updateKeyword(idx, e.target.value)}
                        data-testid={`input-keyword-${idx}`}
                      />
                      {onboardingKeywords.length > 1 && (
                        <Button variant="ghost" size="icon" className="shrink-0" onClick={() => removeKeyword(idx)} data-testid={`button-remove-keyword-${idx}`}>
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {onboardingKeywords.length < 5 && (
                <Button variant="outline" size="sm" onClick={addKeyword} data-testid="button-add-keyword">
                  <Plus className="w-4 h-4 mr-1" />
                  Add Keyword
                </Button>
              )}

              <div className="p-3 rounded-xl bg-brand-blue/5 border border-brand-blue/20">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-brand-blue shrink-0" />
                  <span className="text-sm text-muted-foreground">
                    We'll scan a 7x7 grid at 5-mile radius for each keyword — completely free!
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between pt-2">
                <Button variant="ghost" onClick={() => setStep(4)} data-testid="button-back-step5">
                  Back
                </Button>
                <Button
                  className="bg-brand-orange hover:bg-brand-orange/90"
                  onClick={() => setStep(6)}
                  disabled={onboardingKeywords.filter(k => k.trim()).length === 0}
                  data-testid="button-continue-step5"
                >
                  Continue
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 6 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-500" />
                Join the Leaderboard
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-6 rounded-xl bg-gradient-to-br from-amber-500/10 via-brand-orange/5 to-brand-blue/10 border border-amber-500/20 text-center space-y-4">
                <div className="w-16 h-16 rounded-2xl bg-amber-500/20 flex items-center justify-center mx-auto">
                  <Trophy className="w-8 h-8 text-amber-500" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">Compete with other businesses</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    See how your Google presence stacks up against other businesses on the platform. Earn tokens, build streaks, and climb the ranks!
                  </p>
                </div>
                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                  <div className="p-3 rounded-xl bg-background/50 border border-border/30">
                    <Sparkles className="w-5 h-5 text-brand-orange mx-auto mb-1" />
                    <p className="text-xs font-medium">Earn Tokens</p>
                  </div>
                  <div className="p-3 rounded-xl bg-background/50 border border-border/30">
                    <Target className="w-5 h-5 text-brand-blue mx-auto mb-1" />
                    <p className="text-xs font-medium">Track Progress</p>
                  </div>
                  <div className="p-3 rounded-xl bg-background/50 border border-border/30">
                    <Star className="w-5 h-5 text-amber-500 mx-auto mb-1" />
                    <p className="text-xs font-medium">Unlock Badges</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 rounded-xl border border-border/50">
                <div className="flex items-center gap-3">
                  <Switch
                    id="leaderboard-opt-in"
                    checked={leaderboardOptIn}
                    onCheckedChange={setLeaderboardOptIn}
                    data-testid="switch-leaderboard"
                  />
                  <Label htmlFor="leaderboard-opt-in" className="text-sm font-medium cursor-pointer">
                    Opt in to Leaderboard
                  </Label>
                </div>
                {leaderboardOptIn && (
                  <Badge className="bg-emerald-500/20 text-emerald-400 text-[10px]">
                    <Check className="w-3 h-3 mr-1" />
                    Opted In
                  </Badge>
                )}
              </div>

              <div className="p-3 rounded-xl bg-brand-orange/5 border border-brand-orange/20">
                <div className="flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-brand-orange shrink-0" />
                  <span className="text-sm font-medium">Complete setup to earn <span className="text-brand-orange font-bold">+49 tokens</span></span>
                </div>
                <p className="text-xs text-muted-foreground mt-1 ml-6">Tokens unlock badges and boost your leaderboard rank</p>
              </div>

              <div className="flex items-center justify-between pt-2">
                <Button variant="ghost" onClick={() => setStep(5)} data-testid="button-back-step6">
                  Back
                </Button>
                <Button
                  className="bg-brand-orange hover:bg-brand-orange/90"
                  onClick={handleSaveLeaderboard}
                  disabled={updateLocationMutation.isPending || runScansMutation.isPending}
                  data-testid="button-finish-setup"
                >
                  {(updateLocationMutation.isPending || runScansMutation.isPending) ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  ) : (
                    <Rocket className="w-4 h-4 mr-1" />
                  )}
                  Launch & Run Scans
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 7 && (
          scanIds.length > 0 ? (
            <GeoGridResultsStep
              scanIds={scanIds}
              onComplete={handleLoadingComplete}
            />
          ) : (
            <LoadingScreen onComplete={handleLoadingComplete} tokensAwarded={tokensAwarded} />
          )
        )}

        <SuspendedDialog
          open={showSuspendedDialog}
          onClose={() => { setShowSuspendedDialog(false); setSuspendedLocation(null); }}
          onContinue={handleSuspendedContinue}
          isSuspended={suspendedLocation?.verificationState === "SUSPENDED" || suspendedLocation?.gbpStatus === "disabled"}
        />

        <BillingModal
          open={showBillingModal}
          onClose={() => setShowBillingModal(false)}
          onStartTrial={handleStartTrial}
          isPending={trialMutation.isPending || setupMutation.isPending}
        />
      </div>
      <AriaChat />
    </div>
  );
}
